import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { RowConfig } from '@/shared/types/table';
import {
  toggleRowSelection,
  isAllSelected,
  isSomeSelected,
  applyPageSelectAll,
} from '../utils/selection';

export interface UseTableSelectionOptions<T> {
  /** Si devuelve false, la fila no se puede seleccionar (checkbox deshabilitado en UI). */
  isRowSelectable?: (row: RowConfig<T>) => boolean;
  /**
   * Called whenever the table selection ids change (initial mount included, with `[]` until rows are selected).
   * Uses a ref so callers may pass an inline handler without re-running this effect every render.
   */
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  /**
   * When defined (including `[]`), selection is controlled by the parent: it must update this list in response
   * to `onSelectionChange`. Fixes consumers that pass `selection.selectedIds` while the table used to ignore it.
   */
  controlledSelectedIds?: (string | number)[];
  /**
   * Si true (default), el checkbox del header acumula la página visible sin borrar otras páginas.
   * Si false, "seleccionar todo" reemplaza la selección por la página visible y al desmarcar limpia todo.
   */
  accumulativeSelection?: boolean;
}

export const useTableSelection = <T>(
  rows: RowConfig<T>[],
  multiple = true,
  options?: UseTableSelectionOptions<T>
) => {
  const isRowSelectable = options?.isRowSelectable;
  const controlledSelectedIds = options?.controlledSelectedIds;
  const isControlled = controlledSelectedIds !== undefined;
  const accumulativeSelection = options?.accumulativeSelection ?? true;

  const [internalSelectedIds, setInternalSelectedIds] = useState<(string | number)[]>([]);

  const selectedIds = isControlled ? controlledSelectedIds! : internalSelectedIds;

  const onSelectionChangeRef = useRef(options?.onSelectionChange);
  onSelectionChangeRef.current = options?.onSelectionChange;

  const selectableRows = useMemo(
    () => (isRowSelectable ? rows.filter((row) => isRowSelectable(row)) : rows),
    [rows, isRowSelectable]
  );

  const allSelected = useMemo(
    () => isAllSelected(selectableRows, selectedIds),
    [selectableRows, selectedIds]
  );

  const someSelected = useMemo(
    () => isSomeSelected(selectableRows, selectedIds),
    [selectableRows, selectedIds]
  );

  // Drop ids only when the row is visible in `rows` and became non-selectable (excluded/disabled).
  // When `rows` is a paginated slice, absent ids must be kept — otherwise multi-page selection clears.
  useEffect(() => {
    if (!isRowSelectable) return;
    const filtered = selectedIds.filter((id) => {
      const row = rows.find((r) => r.id === id);
      if (!row) return true;
      return isRowSelectable(row);
    });
    if (filtered.length === selectedIds.length) return;
    if (isControlled) {
      onSelectionChangeRef.current?.(filtered);
    } else {
      setInternalSelectedIds(filtered);
    }
  }, [rows, isRowSelectable, isControlled, selectedIds]);

  useEffect(() => {
    if (isControlled) return;
    onSelectionChangeRef.current?.(internalSelectedIds);
  }, [isControlled, internalSelectedIds]);

  const handleRowSelect = useCallback(
    (row: RowConfig<T>) => {
      if (isRowSelectable && !isRowSelectable(row)) return;
      if (!multiple) {
        const next = [row.id];
        if (isControlled) {
          onSelectionChangeRef.current?.(next);
        } else {
          setInternalSelectedIds(next);
        }
        return;
      }
      if (isControlled) {
        const next = toggleRowSelection(selectedIds, row.id);
        onSelectionChangeRef.current?.(next);
      } else {
        setInternalSelectedIds((prev) => toggleRowSelection(prev, row.id));
      }
    },
    [multiple, isRowSelectable, isControlled, selectedIds]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      const next = applyPageSelectAll(checked, selectedIds, selectableRows, accumulativeSelection);
      if (isControlled) {
        onSelectionChangeRef.current?.(next);
      } else {
        setInternalSelectedIds(next);
      }
    },
    [selectableRows, isControlled, selectedIds, accumulativeSelection]
  );

  const clearSelection = useCallback(() => {
    if (isControlled) {
      onSelectionChangeRef.current?.([]);
    } else {
      setInternalSelectedIds([]);
    }
  }, [isControlled]);

  const selectRows = useCallback(
    (ids: (string | number)[]) => {
      if (isControlled) {
        onSelectionChangeRef.current?.(ids);
      } else {
        setInternalSelectedIds(ids);
      }
    },
    [isControlled]
  );

  const isSelected = useCallback(
    (rowId: string | number) => selectedIds.includes(rowId),
    [selectedIds]
  );

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    allSelected,
    someSelected,
    handleRowSelect,
    handleSelectAll,
    clearSelection,
    selectRows,
    isSelected,
  };
};
